import {
    Inject,
    AfterViewChecked,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ValidationErrors } from '@angular/forms';
import {
    APPOINTMENT_SLOT_STATUS,
    AppointmentBookingConfigs,
    AppointmentSlotsModel,
    assignModelOptions,
    ConfigConstants,
    ConfirmationFollowupModel,
    ConnectivityService,
    EventService,
    ModalClass,
    ConfigInstanceToken,
    ConfigInterface,
} from '@evorait/pace-core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ConfirmationAppointmentComponent } from './appointment/confirmation-appointment.component';
import moment from 'moment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'pace-confirmation-followup',
    templateUrl: './confirmation-followup.component.html',
    styleUrls: ['./confirmation-followup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ConfirmationFollowupComponent implements OnInit, OnDestroy, AfterViewChecked {
    @Input()
    public model: ConfirmationFollowupModel;

    @Input()
    public parentForm: FormArray;

    // form
    public followupForm: FormGroup;
    public followupFormChildren: FormArray;

    public strings: {
        followupHeader: string;
    };

    public readonly optionValueYes = ConfigConstants.optionValueYes; // defining it here as this is needed in the template.
    public readonly optionValueNo = ConfigConstants.optionValueNo; // defining it here as this is needed in the template.
    public showFollowUpAppointment: boolean;
    public showUMRCodeDropdown: boolean;
    public isFollowUpSelectedNo: boolean;
    public showStartImmediately: boolean;
    public showRemoteWork: boolean;
    private destroy$ = new Subject<void>();

    public constructor(
        private readonly translate: TranslateService,
        private readonly eventService: EventService,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly formBuilder: FormBuilder,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        private readonly modalService: BsModalService,
        private readonly connectivityService: ConnectivityService,
        private readonly destroyRef: DestroyRef,
    ) {}

    public ngOnInit(): void {
        this.strings = {
            followupHeader: <string>this.translate.instant('CONFIRMATION_FOLLOWUP_LABEL'),
        };

        const appBookingValidator = (): ValidationErrors | null => this.checkAppBookingValidity();
        this.followupFormChildren = this.formBuilder.array([]);
        this.followupForm = this.formBuilder.group(
            {
                children: this.followupFormChildren,
            },
            { validators: appBookingValidator },
        );

        this.parentForm.push(this.followupForm);

        this.showStartImmediately = this.configService.getShowStartImmediatelyFollowUp();
        this.showRemoteWork = this.configService.getShowRemoteWork();

        this.eventService.materialConsumptionOrNotReceivedChanged$
            .pipe(takeUntil(this.destroy$))
            .subscribe((materialList) => {
                this.model.materialsList = materialList;
                this.model.updateMedPhysicsEvaluationVisibility();
                this.determineFollowupDependency();
                this.changeDetector.detectChanges();
            });
        this.eventService.equipmentStatusAfterFixChanged$
            .pipe(takeUntil(this.destroy$))
            .subscribe((equipmentStatusAfterFix) => {
                if (typeof this.model.equipmentStatus !== 'undefined') {
                    this.model.equipmentStatus.equipmentAfterFix = equipmentStatusAfterFix;
                }
                this.determineFollowupDependency();
                this.changeDetector.detectChanges();
            });
        this.eventService.equipmentStatusInArrivalChanged$
            .pipe(takeUntil(this.destroy$))
            .subscribe((isEquipmentDependentSA) => {
                this.model.equipmentStatus = isEquipmentDependentSA;
                this.determineFollowupDependency();
                this.changeDetector.detectChanges();
            });

        if (this.model.isAppBookingEnabled) {
            this.connectivityService.isOnline$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isOnline) => {
                if (this.model.isAppointmentConfirmed) {
                    return;
                }
                this.model.appBookingOptions = assignModelOptions(
                    this.model.appBookingOptions,
                    { ...this.model.appBookingOptions.options, readonly: !isOnline },
                    this.model,
                );
                if (!isOnline) {
                    this.model.appBookingOptions.selectByPredicate((option) => option.value === this.optionValueNo);
                }
                this.followupForm.updateValueAndValidity();
                this.changeDetector.detectChanges();
            });

            this.determineFollowupType();
        }
    }

    public ngAfterViewChecked(): void {
        this.showHideControls();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public showHideControls(): void {
        this.showFollowUpAppointment = this.model.followupOptions.selectedEntry.value === this.optionValueYes;
        this.isFollowUpSelectedNo = this.model.followupOptions.selectedEntry.value === this.optionValueNo;
        if (!this.isFollowUpSelectedNo) {
            if (this.model.reasonsCode && this.model.reasonsCode.selectedEntry) {
                if (
                    this.model.reasonsCode.selectedEntry.payload.codeGroup.value.indexOf(this.model.assayIndicator) ===
                    0
                ) {
                    // clear the dependent input fields
                    this.model.assayNumber.value = '';
                    this.model.lotNumber.value = '';
                }
                // deselect the selected item in tree model
                this.model.reasonsCode.selectEntry(this.model.reasonsCode.selectedEntry);
            } else if (this.model.reasonsCodeFallback && this.model.reasonsCodeFallback.selectedEntry) {
                this.model.reasonsCodeFallback.deselect();
            }
            if (this.model.showLegalCheckHandover) {
                const initialSelection = 2;
                this.model.legalCheckHandoverUserDocumentation.selectByIndex(initialSelection);
                this.model.legalCheckHandoverSafetyInformation.selectByIndex(initialSelection);
            }
        }
        if (typeof this.model.followUpUMRCodes !== 'undefined') {
            this.showUMRCodeDropdown = this.model.followupOptions.selectedEntry.value === this.optionValueNo;
            if (!this.showUMRCodeDropdown) {
                this.model.followUpUMRCodes.deselect();
            } else {
                // set the selected entry again
                if (this.model.followUpUMRCodes) {
                    this.model.followUpUMRCodes.restore();
                }
            }
        }
        if (typeof this.model.followUpReasons !== 'undefined' && !this.showFollowUpAppointment) {
            this.model.followUpReasons.deselect();
        } else {
            // set the selected entry again
            if (this.model.followUpReasons) {
                this.model.followUpReasons.restore();
            }
        }
        if (typeof this.model.additionalInfo !== 'undefined') {
            // clear the Additional Info field if followup is not Yes
            if (this.model.followupOptions.selectedEntry.value !== this.optionValueYes) {
                this.model.additionalInfo.value = '';
            }
        }

        this.changeDetector.detectChanges();
    }

    private determineFollowupDependency(): void {
        this.model.determineFollowupDependency();
        this.showHideControls();
        this.followupForm.updateValueAndValidity();
    }

    public determineFollowupType(): void {
        this.model.determineFollowupType();
        this.followupForm.updateValueAndValidity();
        this.changeDetector.detectChanges();
    }

    public openAppointmentModal(): void {
        const followUpShortText = this.model.getFollowUpShortTextForOutModel();
        const followUpLongText = this.model.getCrmLongTextTRTESetForOutModel();

        const appBookingConfigs = this.getAppointmentBookingConfigs();
        const modalInstance = this.modalService.show(ConfirmationAppointmentComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            initialState: {
                model: new AppointmentSlotsModel(
                    this.model.notification.getNotificationState().identifier,
                    followUpShortText,
                    followUpLongText,
                    this.model.appBookingDuration,
                    appBookingConfigs,
                    this.model.isDedicatedResourceEnabled,
                    this.model.defaultDedicatedResource,
                ),
            },
            ariaLabelledBy: 'internal-info-modal-title',
        });

        modalInstance.content?.modalResult.pipe(take(1)).subscribe((result) => {
            this.model.appBookingDuration = result.duration.value?.asMinutes() ?? this.model.appBookingDuration;
            if (result.dedicatedResource) {
                this.model.defaultDedicatedResource =
                    result.dedicatedResource.getSelectedValue() === this.optionValueYes;
            }
            if (result.isAppointmentBooked) {
                const selectedSlot = result.slotIntervals.value.find(
                    (slot) => slot.status === APPOINTMENT_SLOT_STATUS.AVAILABLE,
                );
                if (selectedSlot) {
                    // appointment booking date and time will be in utc. So we need to convert it to local time
                    this.model.appointmentDate.value = moment.utc(selectedSlot.startDateAndTime).local();
                    this.model.appointmentTime.value = moment.utc(selectedSlot.startDateAndTime).local();
                    this.model.setFollowupFieldsToReadonly();
                    this.eventService.triggerAppointmentConfirmation();
                    this.followupForm.updateValueAndValidity();
                }
            }
            this.changeDetector.markForCheck();
            this.changeDetector.detectChanges();
        });
    }

    private getAppointmentBookingConfigs(): AppointmentBookingConfigs {
        return {
            minDate: this.configService.getAppBookingMinDate(),
            maxDate: this.configService.getAppBookingMaxDate(),
            minAdditionalTime: this.configService.getFollowupMinAdditionalTime(),
            maxDuration: this.configService.getAppBookingMaxDuration(),
            additionalTimeStep: this.configService.getFollowupAdditionalTimeStep(),
        };
    }

    public triggerValidationOnReasonsCodeChange(): void {
        this.changeDetector.detectChanges();
    }

    private checkAppBookingValidity(): null | ValidationErrors {
        const errors: ValidationErrors = {};
        if (
            this.model.followupOptions.getSelectedValue() === this.optionValueYes &&
            this.model.appBookingOptions?.getSelectedValue() === this.optionValueYes &&
            !this.model.isAppointmentConfirmed
        ) {
            errors.required = `please book an appointment`;
            return errors;
        }
        return null;
    }
}
